<template>
  <CCard>
    <CCardHeader>
      <CIcon name="cil-chat-bubble" />
      <h5 class="d-inline ml-2">Message List</h5>
      <div class="card-header-actions">
        <CButton color="primary" @click="createNewMessage">
          <CIcon name="cil-plus" size="lg" /><strong>New</strong> Message
        </CButton>
      </div>
    </CCardHeader>

    <CCardBody>
      <div v-if="mounting" class="mb-4">
        <CSpinner color="primary" />
      </div>

      <div v-else>
        <!-- Filters -->
        <CCard class="filters" accent-color="warning">
          <CCardHeader
            role="button"
            class="text-warning shadow-none card-header"
            @click="resetFilters"
          >
            <strong class="m-0"><CIcon :name="`cil-filter${$store.state.filterShow ? '-x' : ''}`" /> Filters</strong>
            <div class="card-header-actions">
              <CIcon :name="`cil-chevron-${$store.state.filterShow ? 'bottom' : 'top'}`"/>
            </div>
          </CCardHeader>

          <CCollapse :show="$store.state.filterShow">
            <CCardBody class="p-2">
              <div class="filters-grid">
                <!-- Restaurant Filter -->
                <div>
                  <v-select
                    class="v-select-filter"
                    placeholder="Select restaurant.."
                    v-model="selectedRestaurant"
                    :options="allRestaurants"
                    :loading="loading && allRestaurants.length <= 0"
                    @input="restaurantFilter"
                  />
                </div>
                
                <!-- Segment Filter -->
                <div>
                  <v-select
                    class="v-select-filter"
                    placeholder="Select segment.."
                    v-model="selectedSegment"
                    :options="$globalVar.messageSegments"
                    :reduce="s => s.value"
                    :searchable="false"
                    @input="segmentFilter"
                  >
                    <template #option="{ label, description }">
                      {{ label }}
                      <cite v-if="description" class="d-block ml-2 font-weight-normal text-muted"
                        style="font-size: 0.7em; font-size: calc(0.6em + 0.2vw);">{{ description }}</cite>
                    </template>
                  </v-select>
                </div>
                
                <!-- Test Messages Filter -->
                <div>
                  <v-select
                    class="v-select-filter"
                    placeholder="All messages.."
                    v-model="isTestFilter"
                    :options="testFilterOptions"
                    :reduce="t => t.value"
                    :searchable="false"
                    @input="testFilter"
                  />
                </div>
              </div>
            </CCardBody>
          </CCollapse>
        </CCard>

        <CDataTable
          class="table-messages"
          striped
          hover
          :items="loadedItems"
          :fields="fields"
          :sorter="{ external: true, resetable: false }"
          :itemsPerPageSelect="{
            external: true,
            values: [5, 15, 25, 50, 100, 250, 500],
          }"
          :items-per-page.sync="itemsPerPage"
          :loading="loading"
          :noItemsView="{
            noResults: 'No filtering results are available!',
            noItems: 'No messages found!',
          }"
          clickable-rows
          @row-clicked="rowClicked"
          @update:sorter-value="sorterValue"
          @pagination-change="paginationChange"
        >
          <!-- Title -->
          <template #title="{item}">
            <td>
              <strong>{{ item.title[defaultLang] }}</strong>
            </td>
          </template>

          <!-- Segment -->
          <template #segment="{item}">
            <td>
              <CBadge v-if="item.data?.is_test" color="warning" class="mr-1">TEST</CBadge>
              <span>{{ getSegmentLabel(item) }} ({{ item.users_count }})</span>
              <div v-if="getAudienceFilters(item).length" class="mt-1 d-flex flex-wrap flex-gap-1">
                <CBadge
                  v-for="filter in getAudienceFilters(item)"
                  :key="`${item.id}-${filter}`"
                  color="info"
                >
                  {{ filter }}
                </CBadge>
              </div>
              <div v-if="getResendConfigs(item).length" class="mt-1 d-flex flex-wrap flex-gap-1">
                <CBadge
                  v-for="config in getResendConfigs(item)"
                  :key="`${item.id}-${config.key}`"
                  :color="getResendConfigBadgeColor(config)"
                  v-c-tooltip="{
                    html: true,
                    content: config.config_key
                  }"
                >
                  {{ config.label }}
                </CBadge>
              </div>
            </td>
          </template>

          <!-- Channels -->
          <template #channels="{item}">
            <td>
              <div class="d-flex flex-gap-1">
                <CBadge v-for="ch in item.channels.split(',')" :key="`${item.id}-${ch}`" color="light">
                  {{ ch }}
                </CBadge>
              </div>
            </td>
          </template>

          <!-- Voucher -->
          <template #voucher="{item}">
            <td>
              <div class="d-flex flex-column">
                <span>{{ item.voucher?.code }}</span>
                <cite class="small">{{ item.voucher?.name }}</cite>
              </div>
            </td>
          </template>

          <!-- Inbox -->
          <template #inbox="{item}">
            <td class="text-center">
              <span v-if="item.inbox">✅</span>
            </td>
          </template>

          <!-- Created at -->
          <template #created_at="{item}">
            <td>
              <span>{{ new Date(item.created_at).toLocaleString() }}</span>
            </td>
          </template>

          <!-- Scheduled time -->
          <template #scheduled_time="{item}">
            <td>
              <span v-if="item.scheduled_time">{{ new Date(item.scheduled_time).toLocaleString() }}</span>
            </td>
          </template>

          <!-- Actions -->
          <template #actions="{item, index}">
            <td>
              <CButton
                size="sm"
                color="info"
                shape="pill"
                v-c-tooltip="{
                  html: true,
                  content: 'Duplicate'
                }"
                @click.stop="duplicate(item)"
              >
                <CIcon name="cil-clone" />
              </CButton>

              <CButton
                v-if="item.is_editable === true"
                class="ml-1"
                size="sm"
                color="primary"
                variant="ghost"
                shape="pill"
                v-c-tooltip="{
                  html: true,
                  content: 'Edit'
                }"
                @click.stop="edit(item)"
              >
                <CIcon name="cil-pencil" />
              </CButton>

              <CButton
                v-if="item.is_schedule_editable === true"
                class="ml-1"
                size="sm"
                color="warning"
                variant="ghost"
                shape="pill"
                v-c-tooltip="{
                  html: true,
                  content: 'Change scheduled time'
                }"
                @click.stop="openScheduleModal(item, index)"
              >
                <CIcon name="cil-clock" />
              </CButton>

              <template v-if="item.is_deletable === true">
                <CSpinner v-if="item.deleting" size="sm" color="danger" class="ml-3" />
                <CButton
                  v-else
                  class="ml-1"
                  size="sm"
                  color="danger"
                  variant="ghost"
                  shape="pill"
                  @click.stop="deleteItem(item, index)"
                >
                  <CIcon name="cil-trash" />
                </CButton>
              </template>
            </td>
          </template>
        </CDataTable>
      </div>

      <CCard class="actions sticky-bottom">
        <CCardBody class="p-2">
          <div class="d-flex flex-wrap align-items-center" style="gap: 0.75rem">
            <div>
              <h5 class="mt-1">Total: <span class="d-inline count bg-primary pb-1">{{ total }}</span></h5>
            </div>

            <div v-if="pages > 1" class="ml-auto">
              <CPagination
                align="center"
                :dots='false'
                :pages="pages"
                :active-page.sync="activePage"
              />
            </div>
          </div>
        </CCardBody>
      </CCard>
    </CCardBody>

    <CModal
      class="modal-message"
      :title="modalTitle"
      :show.sync="showMessage"
      :closeOnBackdrop="false"
      size="lg"
      color="dark"
    >
      <template v-if="selectedItem">
        <CTabs v-if="showMessage" :active-tab.sync="activeTab">
          <CTab title="Stats">
            <ul class="msg-stats list-group list-group-flush">
              <li class="list-group-item">
                <strong>Audience:</strong> {{ getSegmentLabel(selectedItem) }} ({{ selectedItem.users_count }})
                <div v-if="getAudienceFilters(selectedItem).length" class="mt-2 d-flex flex-wrap flex-gap-1">
                  <CBadge
                    v-for="filter in getAudienceFilters(selectedItem)"
                    :key="`modal-${selectedItem.id}-${filter}`"
                    color="info"
                  >
                    {{ filter }}
                  </CBadge>
                </div>
              </li>
              <li v-if="getResendConfigs(selectedItem).length" class="list-group-item">
                <strong>Resend configs:</strong>
                <div class="mt-2 d-flex flex-wrap flex-gap-1">
                  <CBadge
                    v-for="config in getResendConfigs(selectedItem)"
                    :key="`modal-${selectedItem.id}-${config.key}`"
                    :color="getResendConfigBadgeColor(config)"
                    v-c-tooltip="{
                      html: true,
                      content: config.config_key
                    }"
                  >
                    {{ config.label }}
                  </CBadge>
                </div>
              </li>
              <li class="list-group-item">
                <div class="d-flex flex-wrap align-items-center" style="gap: 0.5rem">
                  <span><strong>Sent/processed:</strong> {{ formatSentCount(selectedItem) }}</span>
                  <CButton
                    size="sm"
                    color="info"
                    variant="outline"
                    shape="pill"
                    :disabled="selectedItem.sentCountLoading"
                    v-c-tooltip="{
                      html: true,
                      content: 'Refresh sent count'
                    }"
                    @click.stop="calculateSentCount(selectedItem)"
                  >
                    <CSpinner v-if="selectedItem.sentCountLoading" size="sm" />
                    <CIcon v-else name="cil-sync" />
                  </CButton>
                </div>
              </li>
              <template v-if="selectedItem.channels === 'email'">
                <li class="list-group-item msg-stats-action">
                  <button
                    type="button"
                    class="msg-stats-button"
                    :class="{ active: engagementMetric === 'viewed' }"
                    @click.stop="selectEngagementMetric('viewed')"
                  >
                    <strong>Viewed:</strong> {{ selectedItem.viewed_count || 0 }}
                  </button>
                </li>
                <li class="list-group-item msg-stats-action">
                  <button
                    type="button"
                    class="msg-stats-button"
                    :class="{ active: engagementMetric === 'clicked' }"
                    @click.stop="selectEngagementMetric('clicked')"
                  >
                    <strong>Clicked:</strong> {{ selectedItem.clicked_count || 0 }}
                  </button>
                </li>
              </template>
              <li v-else-if="selectedItem.channels === 'push'" class="list-group-item msg-stats-action">
                <button
                  type="button"
                  class="msg-stats-button"
                  :class="{ active: notifiedDevicesVisible }"
                  @click.stop="selectNotifiedDevices"
                >
                  <strong>Notified:</strong> {{ selectedItem.notified_count || 0 }}
                </button>
              </li>
              <li class="list-group-item" v-if="selectedItem.inbox"><strong>Read:</strong> {{ selectedItem.is_read_count }}</li>
            </ul>

            <div v-if="engagementMetric" class="msg-engagement">
              <div class="d-flex flex-wrap align-items-center mb-2" style="gap: 0.5rem">
                <h6 class="mb-0">{{ engagementTitle }} <span class="text-muted">({{ engagementTotal }})</span></h6>
                <CSpinner v-if="engagementLoading" size="sm" color="primary" />
              </div>

              <div class="table-responsive">
                <table class="table table-sm table-striped table-hover mb-0">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Lang</th>
                      <th>Country</th>
                      <th>IP</th>
                      <th>Latest order</th>
                      <th>Sent</th>
                      <th>Last activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="user in engagementItems" :key="user.pivot_id">
                      <td>
                        <a
                          class="msg-engagement-user-link"
                          :href="userPageUrl(user.user_id)"
                          target="_blank"
                          rel="noopener"
                          @click.stop
                        >
                          <strong>{{ displayEngagementUserName(user) }}</strong>
                        </a>
                        <span class="d-block text-muted">#{{ user.user_id }}</span>
                      </td>
                      <td class="msg-engagement-email">{{ user.email || '-' }}</td>
                      <td>{{ user.lang || '-' }}</td>
                      <td>{{ user.country || '-' }}</td>
                      <td>{{ user.ip || '-' }}</td>
                      <td>{{ formatDateTime(user.latest_order_at) }}</td>
                      <td>{{ formatDateTime(user.sent_at) }}</td>
                      <td>{{ formatDateTime(user.interacted_at) }}</td>
                    </tr>
                    <tr v-if="!engagementLoading && engagementItems.length === 0">
                      <td colspan="8" class="text-center text-muted py-3">No users found.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div v-if="engagementPages > 1" class="mt-2">
                <CPagination
                  align="center"
                  size="sm"
                  :dots="false"
                  :pages="engagementPages"
                  :active-page.sync="engagementPage"
                />
              </div>
            </div>

            <div v-if="notifiedDevicesVisible" class="msg-engagement">
              <div class="d-flex flex-wrap align-items-center mb-2" style="gap: 0.5rem">
                <h6 class="mb-0">Notified devices <span class="text-muted">({{ notifiedDevicesTotal }})</span></h6>
                <CSpinner v-if="notifiedDevicesLoading" size="sm" color="primary" />
              </div>

              <div class="table-responsive">
                <table class="table table-sm table-striped table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Recipient</th>
                      <th>Latest order</th>
                      <th>Devices</th>
                      <th>Users</th>
                      <th>Sent</th>
                      <th>Notified</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="recipient in notifiedDevicesItems" :key="recipient.pivot_id">
                      <td>
                        <strong>{{ recipient.recipient_type === 'guest_device' ? 'Guest device' : 'User' }}</strong>
                        <span class="d-block text-muted">#{{ recipient.recipient_id }}</span>
                      </td>
                      <td>{{ formatDateTime(recipient.latest_order_at) }}</td>
                      <td class="msg-device-cell">
                        <div
                          v-for="device in notifiedRecipientDevices(recipient)"
                          :key="`notified-device-${recipient.pivot_id}-${device.id}`"
                          class="mb-1"
                        >
                          <strong>#{{ device.id }}</strong> {{ displayDeviceName(device) }}
                          <CBadge :color="device.is_notify ? 'success' : 'secondary'" class="ml-1">
                            {{ device.is_notify ? 'Notify On' : 'Notify Off' }}
                          </CBadge>
                          <div class="text-muted">{{ displayDeviceMeta(device) }}</div>
                          <div class="text-muted">Latest order: {{ formatDateTime(device.latest_order_at) }}</div>
                          <div class="text-muted">Last active: {{ formatDateTime(device.last_active || device.updated_at) }}</div>
                        </div>
                        <span v-if="notifiedRecipientDevices(recipient).length === 0" class="text-muted">
                          No pushable devices
                        </span>
                      </td>
                      <td class="msg-device-users">
                        <div
                          v-for="user in notifiedRecipientUsers(recipient)"
                          :key="`notified-user-${recipient.pivot_id}-${user.id}`"
                          class="mb-1"
                        >
                          <a
                            class="msg-engagement-user-link"
                            :href="userPageUrl(user.id)"
                            target="_blank"
                            rel="noopener"
                            @click.stop
                          >
                            <strong>{{ displayLinkedUserName(user) }}</strong>
                          </a>
                          <span class="d-block text-muted">#{{ user.id }} {{ user.email || '' }}</span>
                          <span class="d-block text-muted">Latest order: {{ formatDateTime(user.latest_order_at) }}</span>
                        </div>
                        <span v-if="notifiedRecipientUsers(recipient).length === 0" class="text-muted">-</span>
                      </td>
                      <td>{{ formatDateTime(recipient.sent_at) }}</td>
                      <td>{{ formatDateTime(recipient.notified_at) }}</td>
                    </tr>
                    <tr v-if="!notifiedDevicesLoading && notifiedDevicesItems.length === 0">
                      <td colspan="6" class="text-center text-muted py-3">No devices found.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div v-if="notifiedDevicesPages > 1" class="mt-2">
                <CPagination
                  align="center"
                  size="sm"
                  :dots="false"
                  :pages="notifiedDevicesPages"
                  :active-page.sync="notifiedDevicesPage"
                />
              </div>
            </div>
          </CTab>

          <CTab v-for="(message, key) in selectedItem.message" :key="key" :title="key">
            <div class="p-2" v-html="message"></div>
          </CTab>
        </CTabs>
      </template>

      <template #footer class="p-1">
        <CButton @click="showMessage = false" color="link">Close</CButton>
      </template>
    </CModal>

    <CModal
      class="schedule-send-modal"
      title="Change scheduled time"
      color="white"
      :show.sync="showScheduleModal"
      :closeOnBackdrop="false"
    >
      <div v-if="scheduleItem" class="d-flex flex-column flex-md-row">
        <v-date-picker
          mode="dateTime"
          is24hr
          :minute-increment="5"
          v-model="scheduleTime"
          :disabled-dates="{
            end: new Date().setDate(new Date().getDate() - 1),
          }"
        />

        <div v-if="scheduleTime" class="lead p-3 w-100">
          <span class="d-block border-bottom pb-2 mb-4">{{ scheduleMoment.format("MMM Do YY") }}</span>
          <span class="d-block border-bottom pb-2 mb-4">{{ scheduleMoment.format('LT') }}</span>
          <span v-if="isScheduleTimeInvalid" class="d-block text-danger">Invalid date & time</span>
          <span v-else class="d-block">* {{ scheduleMoment.fromNow() }}</span>
        </div>
      </div>

      <template #footer>
        <CButton @click="closeScheduleModal" color="link">Cancel</CButton>
        <CButton
          @click="updateSchedule"
          :disabled="scheduleUpdating || isScheduleTimeInvalid"
          color="primary"
          shape="pill"
        >
          <CSpinner v-if="scheduleUpdating" size="sm" class="mr-1" />
          Save
        </CButton>
      </template>
    </CModal>
  </CCard>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { DatePicker } from 'v-calendar';

export default {
  name: "MessageList",
  components: {
    'v-date-picker': DatePicker,
  },

  data() {
    return {
      selectedItem: null,
      loadedItems: [],

      activePage: 1,
      pages: 1,
      total: 0,
      items_per_page: null,
      orderBy: null,
      direction: null,
      search: null,

      defaultLang: "nl",
      modalTitle: null,
      loading: true,
      mounting: true,
      showMessage: false,
      activeTab: null,
      showScheduleModal: false,
      scheduleItem: null,
      scheduleItemIndex: null,
      scheduleTime: null,
      scheduleUpdating: false,
      engagementMetric: null,
      engagementItems: [],
      engagementLoading: false,
      engagementPage: 1,
      engagementPages: 1,
      engagementTotal: 0,
      engagementItemsPerPage: 10,
      engagementRequestId: 0,
      notifiedDevicesVisible: false,
      notifiedDevicesItems: [],
      notifiedDevicesLoading: false,
      notifiedDevicesPage: 1,
      notifiedDevicesPages: 1,
      notifiedDevicesTotal: 0,
      notifiedDevicesItemsPerPage: 10,
      notifiedDevicesRequestId: 0,
    };
  },

  computed: {
    ...mapGetters('restaurants', ['allRestaurants']),

    fields() {
      return [
        { key: "id", label: "ID", _style: "min-width: 80px; width: 80px;" },
        { key: "title", label: `Title (${this.defaultLang})`, sorter: false, _style: "min-width: 200px;" },
        { key: "segment", sorter: false, _style: "min-width: 180px; width: 240px;" },
        { key: "channels", sorter: false, _style: "min-width: 100px; width: 120px;" },
        { key: "voucher", sorter: false, _style: "min-width: 120px; width: 140px;" },
        { key: "inbox", label: "Inbox", sorter: false, _style: "min-width: 76px; width: 76px; text-align: center;" },
        { key: "created_at", label: "Created", _style: "min-width: 170px; width: 170px;" },
        { key: "scheduled_time", label: "🕑 Scheduled", _style: "min-width: 170px; width: 170px;" },
        { key: "actions", label: "", sorter: false, _style: "min-width: 168px; width: 168px;" },
      ]
    },
    itemsPerPage: {
      get: function () { return this.items_per_page ? this.items_per_page : parseInt(process.env.VUE_APP_ITEMS_PER_PAGE) },
      set: function (newValue) { this.items_per_page = newValue }
    },
    reloadParams() { return [this.activePage] },
    queryPage() { return parseInt(this.$route.query.page) || 1 },

    selectedRestaurant: {
      get: function () { return this.$store.state.restaurants.selectedRestaurant },
      set: function (newValue) { this.$store.commit('restaurants/setSelectedRestaurant', newValue) }
    },
    selectedSegment: {
      get: function () { return this.$store.state.filters.messages.selectedSegment },
      set: function (newValue) { this.$store.state.filters.messages.selectedSegment = newValue }
    },
    isTestFilter: {
      get: function () { return this.$store.state.filters.messages.isTest },
      set: function (newValue) { this.$store.state.filters.messages.isTest = newValue }
    },
    testFilterOptions() {
      return [
        { value: null, label: "All Messages" },
        { value: true, label: "Test Messages Only" },
        { value: false, label: "Real Messages Only" }
      ]
    },
    segmentMap() {
      return Object.fromEntries(
        this.$globalVar.messageSegments.map(s => [s.value, s.label])
      )
    },
    scheduleMoment() {
      return this.scheduleTime ? this.moment(this.scheduleTime) : null;
    },
    isScheduleTimeInvalid() {
      return !this.scheduleMoment || this.scheduleMoment.isSameOrBefore(new Date());
    },
    engagementTitle() {
      return this.engagementMetric === 'clicked' ? 'Clicked users' : 'Viewed users';
    },
  },

  watch: {
    reloadParams() {
      if(this.queryPage != this.activePage) {
        this.$router.push({ name: this.$route.name, query: { page: this.activePage } });
      }
      this.getAllItems();
    },
    activeTab(index) {
      if(index === 0) {
        this.modalTitle = "Stats";
      } else {
        var keys = Object.keys(this.selectedItem?.message);
        this.modalTitle = this.selectedItem?.title[keys[index - 1]];
      }
    },
    showMessage(value) {
      if (!value) {
        this.resetEngagementUsers();
        this.resetNotifiedDevices();
      }
    },
    engagementPage(value, oldValue) {
      if (this.engagementMetric && !this.engagementLoading && value !== oldValue) {
        this.fetchEngagementUsers(this.engagementMetric, value);
      }
    },
    notifiedDevicesPage(value, oldValue) {
      if (this.notifiedDevicesVisible && !this.notifiedDevicesLoading && value !== oldValue) {
        this.fetchNotifiedDevices(value);
      }
    }
  },

  async mounted() {
    await this.fetchAllRestaurants();
    await this.getAllItems();
    this.mounting = false;

  },

  methods: {
    ...mapActions('restaurants', ['fetchAllRestaurants']),

    getAudienceFilters(item) {
      if (item?.segment === 'HighPointUsers') {
        return ['High Point Users', this.formatRetentionVoucherFilter(item?.data || {})].filter(Boolean);
      }

      if (item?.segment === 'InactiveOrderUsers') {
        return this.getRetentionAudienceFilters(item?.data || {});
      }

      if (item?.segment !== 'User') {
        return [this.formatRetentionVoucherFilter(item?.data || {})].filter(Boolean);
      }

      return [
        item?.data?.include_leads ? 'Leads' : null,
        item?.data?.filter_no_recent_orders ? 'Users with No Recent Orders' : null,
        item?.data?.filter_high_point_users ? 'High Point Users' : null,
        item?.data?.filter_not_app_users ? 'Not App Users' : null,
        item?.data?.filter_not_main_site_users ? 'Not Main Site Users' : null,
        item?.data?.filter_subsite_users ? 'Subsite Users' : null,
        this.formatAudienceWaveFilter(item?.data?.filter_wave_total, item?.data?.filter_wave_number),
        this.formatGeoProvinceFilter(item?.data?.filter_geo_province_codes),
        this.formatRetentionVoucherFilter(item?.data || {}),
      ].filter(Boolean);
    },

    formatGeoProvinceFilter(codes) {
      return Array.isArray(codes) && codes.length
        ? `Geo Provinces: ${codes.join(', ')}`
        : null;
    },

    getRetentionAudienceFilters(data = {}) {
      return [
        `Step ${data.retention_step || 1}/4`,
        `No order since ${data.no_recent_days || 30} days`,
        this.formatRetentionMediumFilter(data.retention_order_mediums),
        data.retention_key ? `Key: ${data.retention_key}` : null,
        data.push_suppression_days ? `Push cooldown: ${data.push_suppression_days}d` : null,
        data.email_suppression_days ? `Email cooldown: ${data.email_suppression_days}d` : null,
        data.fallback_after_hours ? `Fallback: ${data.fallback_after_hours}h` : null,
        this.formatMaxUsersFilter(data.max_users),
        this.formatRetentionVoucherFilter(data),
      ].filter(Boolean);
    },

    formatMaxUsersFilter(maxUsers) {
      return maxUsers
        ? `Max users: ${Number(maxUsers).toLocaleString()}`
        : null;
    },

    formatRetentionVoucherFilter(data = {}) {
      if (data.retention_voucher_enabled !== true) {
        return null;
      }

      const amount = data.retention_voucher_discount_amount || 7;
      const days = data.retention_voucher_valid_days || 14;
      const minOrder = data.retention_voucher_min_order_amount || 10;
      const scope = data.retention_voucher_scope === 'app' ? 'app only' : 'all channels';
      const delivery = data.retention_voucher_only_delivery ? ', delivery only' : '';

      return `Voucher: EUR ${amount}, ${days}d, min EUR ${minOrder}, ${scope}${delivery}`;
    },

    formatRetentionMediumFilter(values) {
      const options = {
        0: 'Mainsite (M)',
        1: 'Subsite (S)',
        2: 'App (A)',
      };
      const selected = Array.isArray(values) && values.length ? values.map(Number) : [0, 1, 2];
      const labels = selected.map(value => options[value]).filter(Boolean);

      return labels.length ? `Channels: ${labels.join(', ')}` : null;
    },

    formatAudienceWaveFilter(total, number) {
      return total && number ? `Wave ${number}/${total}` : null;
    },

    getSegmentLabel(item) {
      const segment = item?.segment;
      return this.segmentMap[segment] ?? segment;
    },

    getResendConfigs(item) {
      return Array.isArray(item?.resend_configs) ? item.resend_configs : [];
    },

    getResendConfigBadgeColor(config) {
      if (config?.type === 'campaign') {
        return 'primary';
      }

      if (config?.type === 'menutepay_low_usage') {
        return 'success';
      }

      return config?.channel === 'email' ? 'warning' : 'dark';
    },

    formatSentCount(item) {
      if (item?.sent_count !== undefined && item?.sent_count !== null) {
        return item.sent_count;
      }

      return item?.users_count || 0;
    },

    formatDateTime(value) {
      if (!value) {
        return '-';
      }

      const date = new Date(value);
      return isNaN(date.getTime()) ? '-' : date.toLocaleString();
    },

    displayEngagementUserName(user) {
      const name = user?.name || [user?.first_name, user?.last_name].filter(Boolean).join(' ');
      return name || 'User';
    },

    userPageUrl(userId) {
      if (!userId) {
        return '#';
      }

      return this.$router.resolve({
        name: 'User',
        params: { id: userId },
      }).href;
    },

    displayLinkedUserName(user) {
      const name = user?.name || [user?.first_name, user?.last_name].filter(Boolean).join(' ');
      return name || 'User';
    },

    notifiedRecipientDevices(recipient) {
      if (recipient?.device) {
        return [recipient.device];
      }

      return Array.isArray(recipient?.devices) ? recipient.devices : [];
    },

    notifiedRecipientUsers(recipient) {
      if (recipient?.user) {
        return [recipient.user];
      }

      return Array.isArray(recipient?.linked_users) ? recipient.linked_users : [];
    },

    displayDeviceName(device) {
      return device?.device_type || device?.device_model || 'Device';
    },

    displayDeviceMeta(device) {
      return [
        device?.device_os,
        device?.manufacturer,
        device?.app?.app_id,
        device?.country,
        device?.app_versions ? `v${device.app_versions}` : null,
      ].filter(Boolean).join(' | ') || '-';
    },

    resetEngagementUsers() {
      this.engagementRequestId += 1;
      this.engagementMetric = null;
      this.engagementItems = [];
      this.engagementLoading = false;
      this.engagementPage = 1;
      this.engagementPages = 1;
      this.engagementTotal = 0;
    },

    resetNotifiedDevices() {
      this.notifiedDevicesRequestId += 1;
      this.notifiedDevicesVisible = false;
      this.notifiedDevicesItems = [];
      this.notifiedDevicesLoading = false;
      this.notifiedDevicesPage = 1;
      this.notifiedDevicesPages = 1;
      this.notifiedDevicesTotal = 0;
    },

    selectEngagementMetric(metric) {
      this.resetNotifiedDevices();
      this.fetchEngagementUsers(metric, 1);
    },

    selectNotifiedDevices() {
      this.resetEngagementUsers();
      this.fetchNotifiedDevices(1);
    },

    async fetchEngagementUsers(metric, page = 1) {
      if (!this.selectedItem) {
        return;
      }

      const itemId = this.selectedItem.id;
      const requestId = this.engagementRequestId + 1;
      const url = new URL(this.$backend.CRM_MESSAGES.ENGAGEMENT_USERS.replace("{id}", itemId));
      url.searchParams.append('metric', metric);
      url.searchParams.append('page', page);
      url.searchParams.append('itemsPerPage', this.engagementItemsPerPage);

      this.engagementRequestId = requestId;
      this.engagementMetric = metric;
      this.engagementLoading = true;
      this.engagementItems = [];

      try {
        const { data } = await this.$axios.get(url);

        if (this.engagementRequestId !== requestId || this.engagementMetric !== metric || this.selectedItem?.id !== itemId) {
          return;
        }

        this.engagementItems = data.data || [];
        this.engagementPage = data.current_page || 1;
        this.engagementPages = data.last_page || 1;
        this.engagementTotal = data.total || 0;
      } catch (error) {
        this.engagementItems = [];
        this.engagementPage = 1;
        this.engagementPages = 1;
        this.engagementTotal = 0;
        this.$toast.error(error.response?.data?.message || 'Could not load engagement users.');
      } finally {
        if (this.engagementRequestId === requestId && this.engagementMetric === metric && this.selectedItem?.id === itemId) {
          this.engagementLoading = false;
        }
      }
    },

    async fetchNotifiedDevices(page = 1) {
      if (!this.selectedItem) {
        return;
      }

      const itemId = this.selectedItem.id;
      const requestId = this.notifiedDevicesRequestId + 1;
      const url = new URL(this.$backend.CRM_MESSAGES.NOTIFIED_DEVICES.replace("{id}", itemId));
      url.searchParams.append('page', page);
      url.searchParams.append('itemsPerPage', this.notifiedDevicesItemsPerPage);

      this.notifiedDevicesRequestId = requestId;
      this.notifiedDevicesVisible = true;
      this.notifiedDevicesLoading = true;
      this.notifiedDevicesItems = [];

      try {
        const { data } = await this.$axios.get(url);

        if (this.notifiedDevicesRequestId !== requestId || this.selectedItem?.id !== itemId) {
          return;
        }

        this.notifiedDevicesItems = data.data || [];
        this.notifiedDevicesPage = data.current_page || 1;
        this.notifiedDevicesPages = data.last_page || 1;
        this.notifiedDevicesTotal = data.total || 0;
      } catch (error) {
        this.notifiedDevicesItems = [];
        this.notifiedDevicesPage = 1;
        this.notifiedDevicesPages = 1;
        this.notifiedDevicesTotal = 0;
        this.$toast.error(error.response?.data?.message || 'Could not load notified devices.');
      } finally {
        if (this.notifiedDevicesRequestId === requestId && this.selectedItem?.id === itemId) {
          this.notifiedDevicesLoading = false;
        }
      }
    },

    async getAllItems() {
      this.loading = true;

      var url = new URL(this.$backend.CRM_MESSAGES.GET_ALL),
        params = {
          paginate: true,
          page: this.activePage,
          itemsPerPage: this.itemsPerPage,
          search: this.search,
          'order-by': this.orderBy,
          direction: this.direction,
          restaurant_id: this.selectedRestaurant?.value,
          segment: this.selectedSegment,
          is_test: this.isTestFilter,
        };

      Object.keys(params).forEach((key) => {
        if (
          typeof params[key] !== "undefined" &&
          params[key] !== null &&
          params[key] !== "" &&
          params[key].length !== 0
        )
          url.searchParams.append(key, params[key]);
      });

      await this.$axios.get(url)
        .then((response) => response.data )
        .then((items) => {
          // Son sayfadan daha büyük bir queryPage çağrımı (?page=XXX) yapılırsa;
          if (items.current_page > items.last_page)
            this.activePage = 1;
          else {
            this.loadedItems = items.data.map((el) => {
              return {
                ...el,
                deleting: false,
                sentCountLoading: false,
              };
            });
            this.activePage = items.current_page;
            this.pages = items.last_page;
            this.total = items.total;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    paginationChange(value) {
      this.itemsPerPage = value;
      this.getAllItems();
    },
    sorterValue(item) {
      this.orderBy = item.column;
      this.direction = item.column ? (item.asc ? "asc" : "desc") : null;
      this.getAllItems();
    },
    rowClicked(item, index, column, e) {
      const exclude = [
        "INPUT",
        "LABEL",
        "BUTTON",
        "svg",
        // "path",
        "A",
        // "IMG",
        // "SPAN",
        // "DIV",
        "rect",
        // "STRONG",
      ];

      if (!exclude.includes(e.target.tagName)) {
        // var keys = Object.keys(item?.message);
        // this.activeTab = keys.indexOf(this.defaultLang);
        // this.modalTitle = item?.title[this.defaultLang];

        this.resetEngagementUsers();
        this.resetNotifiedDevices();
        this.activeTab = 0;
        this.selectedItem = item;
        this.showMessage = true;
      }
    },

    buildMessageForm(message) {
      const segment = message.segment === 'HighPointUsers'
        ? 'User'
        : (message.segment !== 'Test' ? message.segment : null);
      const hasUniqueVoucherSetting = Object.prototype.hasOwnProperty.call(
        message.data || {},
        'retention_voucher_enabled'
      );

      var form = {
        // "channels": message.channels.split(','),
        "channels": message.channels.includes(',') ? message.channels.split(',')[0] : message.channels,
        "apps": message.push_apps || [],
        "segment": segment,
        "title": message.title,
        "message": message.message,
        "voucher": message.voucher,
        "put_inbox": message.inbox == 1,
        "active_until": message.active_until?.split(' ')[0],
        "active_until_is_null": message.inbox == 1 && !message.active_until,
        "data": {
          "include_leads": message.data?.include_leads || false,
          "filter_no_recent_orders": message.data?.filter_no_recent_orders || false,
          "filter_high_point_users": message.segment === 'HighPointUsers' || message.data?.filter_high_point_users || false,
          "filter_not_app_users": message.data?.filter_not_app_users || false,
          "filter_not_main_site_users": message.data?.filter_not_main_site_users || false,
          "filter_subsite_users": message.data?.filter_subsite_users || false,
          "filter_geo_province_codes": message.data?.filter_geo_province_codes || [],
          "filter_wave_total": message.data?.filter_wave_total || null,
          "filter_wave_number": message.data?.filter_wave_number || null,
          "restaurant_id": message.data?.restaurant_id || null,
          "no_recent_days": message.data?.no_recent_days || 60,
          "retention_order_mediums": message.data?.retention_order_mediums || [0, 1, 2],
          "retention_key": message.data?.retention_key || "order_users_v1",
          "retention_step": message.data?.retention_step || 1,
          "retention_step_gap_days": message.data?.retention_step_gap_days || 14,
          "push_suppression_days": message.data?.push_suppression_days || 14,
          "email_suppression_days": message.data?.email_suppression_days || 30,
          "fallback_after_hours": message.data?.fallback_after_hours || 24,
          "max_users": message.data?.max_users || null,
          "retention_voucher_enabled": hasUniqueVoucherSetting
            ? message.data?.retention_voucher_enabled
            : message.segment === 'InactiveOrderUsers',
          "retention_voucher_discount_amount": message.data?.retention_voucher_discount_amount || 7,
          "retention_voucher_valid_days": message.data?.retention_voucher_valid_days || 14,
          "retention_voucher_min_order_amount": message.data?.retention_voucher_min_order_amount || 10,
          "retention_voucher_scope": message.data?.retention_voucher_scope || "all",
          "retention_voucher_only_delivery": message.data?.retention_voucher_only_delivery || false,
          "custom_link": {
            "url": message.data?.custom_link?.url || null,
            "title": message.data?.custom_link?.title || null,
          },
          "in_app": message.data?.in_app || false,
          "disable_push_notification": message.data?.disable_push_notification || false,
          "image": message.data?.image || null,
        }
      };

      return form;
    },

    createNewMessage() {
      localStorage.removeItem('user-message');
      this.$router.push({ name: "New Message" });
    },

    duplicate(message) {
      const form = this.buildMessageForm(message);

      localStorage.setItem('user-message', JSON.stringify(form));
      this.$router.push({ name: "New Message" });
    },

    edit(message) {
      if (message.is_editable !== true) {
        this.$toast.error('This message already has recipients and cannot be edited.');
        return;
      }

      const form = this.buildMessageForm(message);
      form._edit_message_id = message.id;

      localStorage.setItem('user-message', JSON.stringify(form));
      this.$router.push({ name: "New Message" });
    },
    async calculateSentCount(item) {
      if (!item || item.sentCountLoading) {
        return;
      }

      const url = this.$backend.CRM_MESSAGES.SENT_COUNT.replace("{id}", item.id);
      this.$set(item, 'sentCountLoading', true);

      try {
        const { data } = await this.$axios.get(url);
        const counts = {
          users_count: data.sent_count,
          sent_count: data.sent_count,
          is_read_count: data.read_count,
          viewed_count: data.viewed_count,
          clicked_count: data.clicked_count,
          notified_count: data.notified_count,
        };

        Object.keys(counts).forEach(key => this.$set(item, key, counts[key]));

        const rowIndex = this.loadedItems.findIndex(row => row.id === item.id);
        if (rowIndex !== -1 && this.loadedItems[rowIndex] !== item) {
          this.$set(this.loadedItems, rowIndex, {
            ...this.loadedItems[rowIndex],
            ...counts,
          });
        }

        this.$toast.success('Sent count refreshed.');
      } catch (error) {
        this.$toast.error(error.response?.data?.message || 'Could not refresh sent count.');
      } finally {
        this.$set(item, 'sentCountLoading', false);
      }
    },
    openScheduleModal(item, index) {
      this.scheduleItem = item;
      this.scheduleItemIndex = index;
      this.scheduleTime = item.scheduled_time ? this.moment(item.scheduled_time).toDate() : null;
      this.showScheduleModal = true;
    },
    closeScheduleModal() {
      this.showScheduleModal = false;
      this.scheduleItem = null;
      this.scheduleItemIndex = null;
      this.scheduleTime = null;
      this.scheduleUpdating = false;
    },
    async updateSchedule() {
      if (!this.scheduleItem || this.isScheduleTimeInvalid) {
        return;
      }

      const updateUrl = this.$backend.CRM_MESSAGES.UPDATE_SCHEDULE.replace("{id}", this.scheduleItem.id);
      this.scheduleUpdating = true;

      try {
        const { data } = await this.$axios.put(updateUrl, {
          scheduled_time: this.scheduleMoment.format('YYYY-MM-DD HH:mm'),
        });

        if (this.scheduleItemIndex !== null && this.scheduleItemIndex !== -1) {
          this.$set(this.loadedItems, this.scheduleItemIndex, {
            ...this.loadedItems[this.scheduleItemIndex],
            ...data,
          });
        }

        if (this.selectedItem?.id === data.id) {
          this.selectedItem = {
            ...this.selectedItem,
            ...data,
          };
        }

        this.$toast.success('Scheduled time updated.');
        this.closeScheduleModal();
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Could not update scheduled time.';
        this.$toast.error(errorMessage);
      } finally {
        this.scheduleUpdating = false;
      }
    },
    async deleteItem(item, index) {
      const confirm = await this.mSwal.fire({
        title: "Are you sure you want to delete?",
        text: "The message will be deleted!",
        icon: "warning",
      });

      if (confirm.isConfirmed) {
        const deleteUrl = this.$backend.CRM_MESSAGES.DELETE.replace("{id}", item.id);
        if (index !== -1) {
          this.$set(this.loadedItems, index, { ...item, deleting: true });
        }

        try {
          const { data } = await this.$axios.delete(deleteUrl);
          index !== -1 && this.$delete(this.loadedItems, index);
          this.$toast.success(data.message);
        } catch (error) {
          if (index !== -1) {
            this.$set(this.loadedItems, index, { ...item, deleting: false });
          }

          if (error.response) {
            this.$toast.error(`${error.response.data.message}`);
          } else if (error.request) {
            this.$toast.error('No response received from the server.');
          } else {
            this.$toast.error('An unexpected error occurred.');
          }
        } finally {
          this.$forceUpdate();
        }
      }
    },

    // Filters
    restaurantFilter() {
      this.getAllItems();
    },
    segmentFilter() {
      this.getAllItems();
    },
    testFilter() {
      this.getAllItems();
    },
    async resetFilters() {
      if (this.$store.state.filterShow) {
        this.selectedRestaurant = null;
        this.selectedSegment = null;
        this.isTestFilter = null;
        this.getAllItems();
      }

      this.$store.commit('toggle', 'filterShow');
    },
  }
};
</script>

<style>
.table-messages td {
  padding: 0.5rem 0.5rem;
  vertical-align: middle;
}

.modal-message .modal-header {
  padding: 0.75rem !important;
}
.modal-message .modal-footer {
  padding: 0.25rem !important;
}

.schedule-send-modal .vc-container {
  box-shadow: none !important;
  border: none !important;
}

ul.msg-stats li {
  font-size: 1.375em;
  padding: 0.75rem 0.375rem;
}

ul.msg-stats li.msg-stats-action {
  padding: 0;
}

.msg-stats-button {
  width: 100%;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-size: 1em;
  padding: 0.75rem 0.375rem;
  text-align: left;
}

.msg-stats-button:hover,
.msg-stats-button.active {
  background: #f3f6fb;
  color: #1f66d1;
}

.msg-engagement {
  border-top: 1px solid #d8dbe0;
  font-size: 0.875rem;
  margin-top: 0.75rem;
  padding-top: 1rem;
}

.msg-engagement th,
.msg-engagement td {
  vertical-align: middle;
  white-space: nowrap;
}

.msg-engagement-email {
  max-width: 220px;
  overflow-wrap: anywhere;
  white-space: normal !important;
}

.msg-device-cell,
.msg-device-users {
  max-width: 320px;
  overflow-wrap: anywhere;
  white-space: normal !important;
}

.msg-engagement-user-link {
  color: inherit;
  text-decoration: none;
}

.msg-engagement-user-link:hover {
  color: #1f66d1;
  text-decoration: underline;
}

.filters-grid {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: 1fr;
}

@media (min-width: 576px) {
  .filters-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* @media (min-width: 1200px) {
  .filters-grid {
    grid-template-columns: repeat(4, 1fr);
  }
} */
</style>
